import { useAtom } from "jotai";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useCustomerDataFetch } from "~/features/quote/hooks/use-quote-hooks.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";

type TenderModalState = {
	confirmation: boolean;
	tender: boolean;
	accountRep: boolean;
	error: boolean;
};

/**
 * Shared modal state machine for the tender-style shipment pages (LTL Shipment,
 * Re-Rate). Owns the confirmation/tender/account-rep/error modal flags plus the
 * post-tender reset + navigate behavior.
 *
 * `resetNavigateRoute` is the route to send the user to after the tender or
 * account-rep modal is dismissed, so each page can diverge on where it lands.
 */
export const useTenderModals = (resetNavigateRoute: string) => {
	const navigate = useNavigate();
	const { resetForm } = useContext(QuoteDetailsContext);
	const [showModal, setShowModal] = useState<TenderModalState>({
		confirmation: false,
		tender: false,
		accountRep: false,
		error: false,
	});
	const [errorMessage, setErrorMessage] = useState("");

	const [formResetKey, setFormResetKey] = useState(0);

	const handleShowConfirmationModal = useCallback((shouldShow = true) => {
		setShowModal((prev) => ({ ...prev, confirmation: shouldShow }));
	}, []);

	const handleShowErrorModal = useCallback(
		(shouldShow: boolean, message = "") => {
			setErrorMessage(message);
			setShowModal((prev) => ({ ...prev, error: shouldShow }));
		},
		[],
	);

	const handleShowTenderModal = useCallback(
		(shouldShow = true) => {
			setShowModal((prev) => ({ ...prev, tender: shouldShow }));

			if (!shouldShow) {
				resetForm();
				setFormResetKey((prev) => prev + 1);
				setTimeout(() => {
					navigate(resetNavigateRoute);
					scrollMainContentToTop();
				}, 500);
			}
		},
		[navigate, resetForm, resetNavigateRoute],
	);

	const handleShowAccountRepModal = useCallback(
		(shouldShow = true) => {
			setShowModal((prev) => ({ ...prev, accountRep: shouldShow }));

			if (!shouldShow) {
				resetForm();
				setFormResetKey((prev) => prev + 1);
				setTimeout(() => {
					navigate(resetNavigateRoute);
					scrollMainContentToTop();
				}, 500);
			}
		},
		[navigate, resetForm, resetNavigateRoute],
	);

	return {
		showModal,
		errorMessage,
		formResetKey,
		handleShowConfirmationModal,
		handleShowErrorModal,
		handleShowTenderModal,
		handleShowAccountRepModal,
	};
};

/**
 * Seeds (and re-seeds) the active customer for external users on the tender-style
 * shipment pages. Runs after the form is reset — e.g. when the post-tender modal
 * is closed and the form is cleared back to a fresh state — so customer-gated
 * features (My Templates, Saved Address search, Saved Commodities) don't stay
 * greyed out. A deliberate external clear nulls `selectedCustomer`, so this won't
 * fight it.
 */
export const useSeedActiveCustomer = () => {
	const { quoteState, setCustomerDetails, setCreditDetails } =
		useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();
	const [selectedCustomer] = useAtom(selectedCustomerAtom);

	useCustomerDataFetch(
		quoteState.customer?.id,
		setCustomerDetails,
		setCreditDetails,
		isInternal,
	);

	useEffect(() => {
		if (!isInternal && selectedCustomer && !quoteState.customer?.id) {
			setCustomerDetails(selectedCustomer);
		}
	}, [
		isInternal,
		setCustomerDetails,
		selectedCustomer,
		quoteState.customer?.id,
	]);
};
